import { Disclosure, Money, StatusPill, Table, type Column } from "../../components";
import type { CheckStatus } from "../../api/types";
import { t } from "../../i18n/t";
import { countChecks } from "./checkCounts";

/** xmg-webux (defect 6): the checks whose key is a source BANK ACCOUNT id (Xero's AccountID, a UUID). */
const BANK_KEYED_CHECKS: ReadonlySet<string> = new Set(["bank", "bank_statement_residual"]);
/** xmg-webux2: the checks whose key is a source CONTACT id; the API names them in `contact_name`. */
const CONTACT_KEYED_CHECKS: ReadonlySet<string> = new Set(["aged_ar", "aged_ap"]);

export interface TotalRow {
  point: string;
  check: string;
  key: string;
  /** Money strings at their stored scale (§A3.5). Never parsed, never re-scaled, never coerced to a float. */
  source: string | null;
  canonical: string | null;
  destination: string | null;
  tolerance: string | null;
  status: CheckStatus;
  /** xmg-gapwording (LABEL): for a `tb`/`fixed_assets` row, whose key is a DESTINATION account code, the
   * destination account and the source account it is mapped from. Absent/null for every other check. */
  label?: string | null;
  /** xmg-webux2: for an `aged_ar`/`aged_ap` row, whose key is the SOURCE contact's id (a Xero ContactID,
   * a UUID), that contact's name from the migration's staged contacts. Absent/null for every other check. */
  contact_name?: string | null;
}

/**
 * The human name for a row's key, when the API sent a label, the key is a known bank account id, or the API
 * named the aged row's contact; `null` when there is none. Shared by the totals tables and the sign-off dialog's
 * failing-check list (xmg-lmux (2)), so both name a row the same way.
 */
export function rowName(row: TotalRow, bankNames?: ReadonlyMap<string, string>): string | null {
  if (row.label !== null && row.label !== undefined && row.label !== "") return row.label;
  if (BANK_KEYED_CHECKS.has(row.check)) return bankNames?.get(row.key) ?? null;
  if (CONTACT_KEYED_CHECKS.has(row.check) && row.contact_name) return row.contact_name;
  return null;
}

/** §B5.1: the rows an accountant must look at come first. INFO sorts LAST -- it is "not applicable",
 * never a comparison an operator needs to look at ahead of an actual PASS (xero-control-legs design). */
const FAILING_FIRST: Readonly<Record<CheckStatus, number>> = {
  MISSING: 0,
  FAIL: 1,
  WARN: 2,
  PASS: 3,
  INFO: 4,
};

export function groupByCheck(rows: readonly TotalRow[]): [string, TotalRow[]][] {
  const groups = new Map<string, TotalRow[]>();
  for (const row of rows) {
    const bucket = groups.get(row.check);
    if (bucket === undefined) {
      groups.set(row.check, [row]);
    } else {
      bucket.push(row);
    }
  }
  return [...groups.entries()].map(([check, group]) => [
    check,
    [...group].sort(
      (a, b) => FAILING_FIRST[a.status] - FAILING_FIRST[b.status] || a.key.localeCompare(b.key),
    ),
  ]);
}

/**
 * §B5.1. One section per comparison **point**, one table per `check` inside it. The caller renders one of these
 * per distinct `point` value in the response; nothing here knows the set of points, because §A8.2 makes `point` a
 * free string that phase 2 widens.
 */
export function TotalsTable({
  point,
  rows,
  currency,
  bankNames,
}: {
  point: string;
  rows: readonly TotalRow[];
  currency: string;
  /** Source bank account id -> "code name", from the scope's `source_bank_accounts` (defect 6). */
  bankNames?: ReadonlyMap<string, string>;
}): JSX.Element {
  const nameOf = (row: TotalRow): string | null => rowName(row, bankNames);
  return (
    <section data-testid={`totals-point-${point}`} aria-label={t("dryrun.point", { point })}>
      <h2>{t("dryrun.point", { point })}</h2>
      {groupByCheck(rows).map(([check, group]) => {
        // xero-control-legs design Critique C7 / Final decision 9: INFO is "not applicable", never a
        // failure. xmg-webux (defect 4): nor is a WARN -- it is counted (and shown) as a warning, from
        // the SAME `countChecks` the tiles, the sign-off card and the dialog use.
        const { pass, fail, warn } = countChecks(group);
        const columns: readonly Column<TotalRow>[] = [
          {
            key: "key",
            header: t("dryrun.col_key"),
            render: (row) => {
              const name = nameOf(row);
              // xmg-webux (defect 6): a bank row keyed by an account id leads with the account's name; the
              // id stays visible, secondary, for anyone matching it against the source. xmg-webux2: an aged
              // AR/AP row keyed by a contact id does the same with the contact's name.
              return name === null || name === row.label ? (
                <span data-testid={`key-${point}-${row.check}-${row.key}`}>{name ?? row.key}</span>
              ) : (
                <span className="cellwrap itemtitle" data-testid={`key-${point}-${row.check}-${row.key}`}>
                  <span className="itemtitle__name">{name}</span>
                  <span className="itemtitle__key mono muted">{row.key}</span>
                </span>
              );
            },
          },
          {
            key: "source",
            header: t("dryrun.col_source"),
            numeric: true,
            render: (row) => <Money value={row.source} currency={currency} />,
          },
          {
            key: "canonical",
            header: t("dryrun.col_canonical"),
            numeric: true,
            render: (row) => <Money value={row.canonical} currency={currency} />,
          },
          {
            key: "destination",
            header: t("dryrun.col_destination"),
            numeric: true,
            render: (row) => <Money value={row.destination} currency={currency} />,
          },
          {
            key: "tolerance",
            header: t("dryrun.col_tolerance"),
            numeric: true,
            render: (row) => (
              <span data-testid={`tolerance-${point}-${row.check}-${row.key}`}>
                <Money value={row.tolerance} currency={currency} />
              </span>
            ),
          },
          {
            key: "status",
            header: t("dryrun.col_status"),
            render: (row) => (
              <span data-testid={`status-${point}-${row.check}-${row.key}`}>
                <StatusPill status={row.status} />
              </span>
            ),
          },
        ];
        return (
          <div key={check} data-testid={`totals-group-${point}-${check}`}>
            {/* xmg-actions-first: a group opens on its own only when it holds a failing check; the summary line
                (the same words as before) is what the closed group shows. */}
            <Disclosure
              open={fail > 0 ? true : undefined}
              attention={fail > 0}
              title={
                <span data-testid={`totals-summary-${point}-${check}`}>
                  {warn === 0
                    ? t("dryrun.group", { check, pass, fail })
                    : t("dryrun.group_warn", { check, pass, fail, warn })}
                </span>
              }
            >
              <div className="stacktable">
                <Table
                  wrap
                  caption={t("dryrun.totals_caption", { point, check })}
                  columns={columns}
                  rows={group}
                  rowKey={(row) => `${row.check}-${row.key}`}
                  emptyMessage={t("dryrun.empty")}
                  // §3: FAIL/MISSING rows take the fail-row tint, in both themes.
                  rowClassName={(row) =>
                    row.status === "FAIL" || row.status === "MISSING" ? "is-fail" : undefined
                  }
                />
              </div>
            </Disclosure>
          </div>
        );
      })}
    </section>
  );
}
